import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { 
  Building2, 
  MapPin, 
  Award, 
  TrendingUp, 
  Users,
  Target,
  BarChart3,
  PieChart as PieChartIcon,
  RefreshCw
} from 'lucide-react';

const API = BACKEND_URL;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
const CERT_COLORS = {
  'level_3_excellence': '#f59e0b',
  'level_2_commitment': '#3b82f6',
  'level_1_basic': '#10b981',
  'none': '#9ca3af'
};

// Custom Tooltip Component - defined outside to avoid recreation on each render
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border rounded-lg shadow-lg">
        <p className="font-medium">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const SocialActorAnalytics = ({ actors = [] }) => {
  // Calculate statistics
  const totalActors = actors.length;
  const certifiedActors = actors.filter(a => a.certification_level && a.certification_level !== 'none').length;
  const avgCompleteness = actors.length > 0 
    ? Math.round(actors.reduce((sum, a) => {
        const fields = ['organization_name', 'organization_type', 'legal_status', 'email', 'city', 'social_mission', 'sdg_alignment'];
        const filled = fields.filter(f => a[f] && (Array.isArray(a[f]) ? a[f].length > 0 : true)).length;
        return sum + (filled / fields.length) * 100;
      }, 0) / actors.length)
    : 0;
  const avgScore = actors.filter(a => a.assessment_scores?.overall).length > 0
    ? Math.round(actors.filter(a => a.assessment_scores?.overall).reduce((sum, a) => sum + (a.assessment_scores?.overall || 0), 0) / actors.filter(a => a.assessment_scores?.overall).length)
    : 0;

  // Organization Type Distribution
  const orgTypeData = Object.entries(
    actors.reduce((acc, actor) => {
      const type = actor.organization_type || 'other';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {})
  ).map(([name, value]) => ({
    name: name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value,
    percentage: Math.round((value / totalActors) * 100)
  }));

  // City Distribution
  const cityData = Object.entries(
    actors.reduce((acc, actor) => {
      const city = actor.city || 'Unknown';
      acc[city] = (acc[city] || 0) + 1;
      return acc;
    }, {})
  ).map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8);

  // Certification Level Distribution
  const certificationData = [
    { name: 'Level A - Excellence', value: actors.filter(a => a.certification_level === 'level_3_excellence').length, color: CERT_COLORS['level_3_excellence'] },
    { name: 'Level B - Commitment', value: actors.filter(a => a.certification_level === 'level_2_commitment').length, color: CERT_COLORS['level_2_commitment'] },
    { name: 'Level C - Basic', value: actors.filter(a => a.certification_level === 'level_1_basic').length, color: CERT_COLORS['level_1_basic'] },
    { name: 'Not Certified', value: actors.filter(a => !a.certification_level || a.certification_level === 'none').length, color: CERT_COLORS['none'] }
  ].filter(d => d.value > 0);

  // SDG Alignment Distribution
  const sdgData = Array.from({ length: 17 }, (_, i) => {
    const sdgNum = i + 1;
    const count = actors.filter(a => a.sdg_alignment?.includes(sdgNum)).length;
    return { name: `SDG ${sdgNum}`, value: count, sdg: sdgNum };
  }).filter(d => d.value > 0).sort((a, b) => b.value - a.value);

  // Score Distribution
  const scoreRanges = [
    { name: '80-100%', min: 80, max: 100, color: '#f59e0b' },
    { name: '60-79%', min: 60, max: 79, color: '#3b82f6' },
    { name: '40-59%', min: 40, max: 59, color: '#10b981' },
    { name: '0-39%', min: 0, max: 39, color: '#9ca3af' }
  ];
  
  const scoreData = scoreRanges.map(range => ({
    name: range.name,
    value: actors.filter(a => {
      const score = a.assessment_scores?.overall;
      return score !== undefined && score >= range.min && score <= range.max;
    }).length,
    color: range.color
  }));

  // Registration Trends (by month - mock data based on created_at)
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const registrationTrends = monthNames.map((month, idx) => {
    const count = actors.filter(a => {
      if (!a.created_at) return false;
      const date = new Date(a.created_at);
      return date.getMonth() === idx;
    }).length;
    return { name: month, registrations: count };
  });

  // Status Distribution
  const statusData = Object.entries(
    actors.reduce((acc, actor) => {
      const status = actor.status || 'draft';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {})
  ).map(([name, value]) => ({
    name: name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
    value
  }));

  return (
    <div className="space-y-6">
      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Organizations</p>
                <p className="text-3xl font-bold text-blue-600">{totalActors}</p>
              </div>
              <Building2 className="h-10 w-10 text-blue-500 opacity-70" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Certified</p>
                <p className="text-3xl font-bold text-green-700">{certifiedActors}</p>
                <p className="text-xs text-gray-500">{totalActors > 0 ? Math.round((certifiedActors/totalActors)*100) : 0}% of total</p>
              </div>
              <Award className="h-10 w-10 text-green-500 opacity-70" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg. Completeness</p>
                <p className="text-3xl font-bold text-purple-600">{avgCompleteness}%</p>
              </div>
              <TrendingUp className="h-10 w-10 text-purple-500 opacity-70" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg. Score</p>
                <p className="text-3xl font-bold text-amber-600">{avgScore}%</p>
              </div>
              <Target className="h-10 w-10 text-amber-700 opacity-70" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Organization Type Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <PieChartIcon className="h-5 w-5 text-blue-600" />
              Organizations by Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {orgTypeData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={orgTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percentage }) => `${name} (${percentage}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {orgTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Certification Level Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Award className="h-5 w-5 text-amber-600" />
              Certification Levels
            </CardTitle>
          </CardHeader>
          <CardContent>
            {certificationData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={certificationData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {certificationData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* City Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <MapPin className="h-5 w-5 text-green-700" />
              Organizations by City
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cityData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={cityData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={100} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* SDG Alignment */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Target className="h-5 w-5 text-purple-600" />
              SDG Alignment (Top 10)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sdgData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sdgData.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} fontSize={10} />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Score Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="h-5 w-5 text-cyan-700" />
              Score Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {scoreData.some(d => d.value > 0) ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={scoreData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {scoreData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No score data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5 text-indigo-600" />
              Status Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {statusData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BarChart3 className="h-5 w-5 text-gray-600" />
            Quick Statistics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{actors.filter(a => a.status === 'approved').length}</p>
              <p className="text-sm text-gray-600">Approved</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-700">{actors.filter(a => a.status === 'pending_approval').length}</p>
              <p className="text-sm text-gray-600">Pending</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-700">{actors.filter(a => a.status === 'certified').length}</p>
              <p className="text-sm text-gray-600">Certified</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-600">{actors.filter(a => a.status === 'draft').length}</p>
              <p className="text-sm text-gray-600">Draft</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SocialActorAnalytics;
